import React, { useContext } from 'react'
import './index.scss'
import { formatBytes } from '@App/helpers/format-bytes'
import { calcNumPages } from '@App/helpers/pagination/calc-num-pages'
import { observer } from 'mobx-react'
import BackButton from '@Components/Buttons/BackButton'
import ContentHeader from '@Components/ContentHeader'
import UploadSelectionSection from '@Components/DocumentUploadForm/UploadSelectionSection'
import ModalPortal from '@Components/Popups/ModalPortal'
import UploadDocumentConfirmationPopup from '@Components/Popups/UploadDocumentConfirmationPopup'
import DuplicateDocumentPopup from '@Components/Popups/ConfirmationPopup/DuplicateDocumentPopup'
import PointPreloader from '@Components/Preloaders/PointPreloader'
import OverclaimDocumentationWarning from '@Scenes/RightsholderScene/RightsholderOverclaim/OverclaimView/OverclaimDocumentUploadForm/OverclaimDocumentationWarning'
import {
    UPLOAD_DOCUMENTS_FORM_ACCEPTED_FILE_TYPES,
    UPLOAD_DOCUMENTS_FORM_MAX_FILE_SIZE_IN_BYTES,
} from '@Scenes/RightsholderScene/RightsholderOverclaim/OverclaimView/OverclaimDocumentUploadForm'
import ExistingDocumentsTabs from './ExistingDocumentsTabs'
import SelectedDocumentsPanel from './SelectedDocumentsPanel'
import { createDocumentSearchCriteriaStore } from './search-config'
import { useOverclaimDocumentUploadPage } from './useOverclaimDocumentUploadPage'
import { StoresContext } from '@App/contexts'

const OverclaimDocumentUploadPage = () => {
    const {
        activeSearchQuery,
        hasRunSearch,
        loadRecentlyUsedDocuments,
        loadSearchedDocuments,
        overclaimViewItem,
        recentlyUsedDocumentsPage,
        recentlyUsedLoading,
        recentlyUsedPagination,
        rightsholderId,
        searchedDocumentsPage,
        searchCriteriaStore,
        searchLoading,
        searchPagination,
        selectedExistingDocumentIds,
        setActiveSearchQuery,
        setHasRunSearch,
        setSearchedDocumentsPage,
        setSearchCriteriaStore,
        toggleExistingDocument,
        uploadState: {
            browseFilePickerRef,
            displayUploadDisclaimer,
            duplicateDocuments,
            isUploading,
            onDuplicateDocumentsContinue,
            onDescriptionChange,
            onFilesChosen,
            onRemove,
            setDisplayUploadDisclaimer,
            uploadDocuments,
            uploadedFiles,
        },
    } = useOverclaimDocumentUploadPage()

    const resetSearchResults = () => {
        setSearchCriteriaStore(createDocumentSearchCriteriaStore())
        setSearchedDocumentsPage(undefined)
        setHasRunSearch(false)
        setActiveSearchQuery({})
    }
    const recentDocumentsPaginationInfo = recentlyUsedDocumentsPage
        ? calcNumPages(
              recentlyUsedDocumentsPage.totalElements,
              recentlyUsedPagination.limit,
              recentlyUsedPagination.page
          )
        : undefined
    const searchedDocumentsPaginationInfo = searchedDocumentsPage
        ? calcNumPages(searchedDocumentsPage.totalElements, searchPagination.limit, searchPagination.page)
        : undefined
    const uploadFileTypesString = UPLOAD_DOCUMENTS_FORM_ACCEPTED_FILE_TYPES.join(', ')
    const maxFileSizeString = formatBytes(UPLOAD_DOCUMENTS_FORM_MAX_FILE_SIZE_IN_BYTES)
    const { userStore } = useContext(StoresContext)

    if (!overclaimViewItem) {
        return <PointPreloader />
    }

    return (
        <div className="overclaim-document-upload-page">
            <ModalPortal isOpen={displayUploadDisclaimer}>
                <UploadDocumentConfirmationPopup
                    onClose={() => setDisplayUploadDisclaimer(false)}
                    confirmationPopupTitle="Upload Documentation"
                    confirmationPopupBody={<OverclaimDocumentationWarning />}
                    onConfirm={uploadDocuments}
                    isDisabled={isUploading || userStore.isInternal}
                />
            </ModalPortal>
            <ModalPortal isOpen={duplicateDocuments.length > 0}>
                <DuplicateDocumentPopup
                    duplicateDocuments={duplicateDocuments}
                    onContinue={onDuplicateDocumentsContinue}
                />
            </ModalPortal>
            <ContentHeader
                title={`Upload Documents - ${overclaimViewItem.title}`}
                navLeft={<BackButton />}
                descriptionElement={
                    <p>
                        Add or select one or more documents to continue. You can upload new files, select a recently
                        used document, or search for previously uploaded documents in the 'Search Existing Documents'
                        tab below.
                    </p>
                }
            />

            <div className="upload-content">
                <div className="upload-column">
                    <UploadSelectionSection
                        onFilesChosen={onFilesChosen}
                        maxFileSizeString={maxFileSizeString}
                        uploadFileTypesString={uploadFileTypesString}
                        allowMultipleUploads
                        browseFilesButtonText="Browse Files"
                        browseFilePickerRef={browseFilePickerRef}
                    />
                </div>
                <SelectedDocumentsPanel
                    browseFilePickerRef={browseFilePickerRef}
                    uploadedFiles={uploadedFiles}
                    isUploading={isUploading}
                    onRemove={onRemove}
                    onDescriptionChange={onDescriptionChange}
                    onSubmit={() => setDisplayUploadDisclaimer(true)}
                />
            </div>
            <ExistingDocumentsTabs
                activeSearchQuery={activeSearchQuery}
                hasRunSearch={hasRunSearch}
                recentlyUsedDocumentsPage={recentlyUsedDocumentsPage}
                recentlyUsedLoading={recentlyUsedLoading}
                recentlyUsedPagination={recentlyUsedPagination}
                recentDocumentsPaginationInfo={recentDocumentsPaginationInfo}
                rightsholderId={rightsholderId}
                currentOverclaimHeaderId={overclaimViewItem.overclaimHeaderId}
                searchCriteriaStore={searchCriteriaStore}
                searchedDocumentsPage={searchedDocumentsPage}
                searchedDocumentsPaginationInfo={searchedDocumentsPaginationInfo}
                searchLoading={searchLoading}
                searchPagination={searchPagination}
                selectedDocumentIds={selectedExistingDocumentIds}
                loadRecentlyUsedDocuments={loadRecentlyUsedDocuments}
                loadSearchedDocuments={loadSearchedDocuments}
                onSelectDocument={(document) => toggleExistingDocument(document, true)}
                onUnselectDocument={(document) => toggleExistingDocument(document, false)}
                resetSearchResults={resetSearchResults}
            />
        </div>
    )
}

export default observer(OverclaimDocumentUploadPage)
